import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, Globe, ChevronDown } from "lucide-react";
import NotificationBell from "../NotificationBell";
import { Settings } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Balance from "./Balance";
import { AddressDisplay } from "./AddressDisplay";
import Breadcrumb from "./Breadcrumb";
import { useAppKitAccount } from "@reown/appkit/react";
import TipHintWrapper from "../TipHint/TipHintWrapper";
import { FlagIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import MenuIcon from "../../assets/icons/menu.svg";
import { Menu } from "lucide-react";
import useSound from "../../Services/SoundService";
// import { FlagIcon } from "react-flag-kit";

const SettingsAppHeaderIcon = () => {
    /**
     * Navigation Settings Icon
     * Goes to Settings Page inside UserProfile.jsx
     */
    const navigate = useNavigate();
    const onClick = () => {
        navigate("/profile/settings");
        window.scrollTo(0, 0);
    };

    return (
        <TipHintWrapper hintKey="settings_icon_tip" location="header">
            <div className="pr-[8px]">
                <Settings className="h-5 w-5 text-zinc-300 " onClick={onClick} />
            </div>
        </TipHintWrapper>
    );
};
const AppHeader = ({ changeLanguage }) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const { address: connectedAddress, isConnected } = useAppKitAccount();
    const dropdownRef = useRef(null);
    const [language, setLanguage] = useState("en");
    const { SoundControl } = useSound("/sounds/place_bid_sound.mp3", "public");
    const { t } = useTranslation();

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
                localStorage.setItem("dropdownOpen", "false"); // Store flag in localStorage
            }
        };

        // Check localStorage on mount to set initial state
        const isDropdownOpenStored = localStorage.getItem("dropdownOpen") === "true";
        setIsDropdownOpen(isDropdownOpenStored);

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);
    useEffect(() => {
        // Add Google Tag Manager script
        const script1 = document.createElement("script");
        script1.async = true;
        script1.src = "https://www.googletagmanager.com/gtag/js?id=G-ZFFHKF5EPD";
        document.head.appendChild(script1);

        const script2 = document.createElement("script");
        script2.innerHTML = `
            window.dataLayer = window.dataLayer || [];
            function gtag(){dataLayer.push(arguments);}
            gtag('js', new Date());
            gtag('config', 'G-ZFFHKF5EPD');
        `;
        document.head.appendChild(script2);

        return () => {
            document.head.removeChild(script1);
            document.head.removeChild(script2);
        };
    }, []);

    // Navigation menus
    const mainNavItems = [
        {
            title: t("nav.auctions"),
            items: [
                { name: t("nav.upcoming"), href: "/" },
                { name: t("nav.live"), href: "/auctions" },
                { name: t("nav.past"), href: "/past" },
            ],
        },
        {
            title: t("nav.staking"),
            items: [
                { name: t("nav.dashboard"), href: "/staking" },
                { name: t("nav.analytics"), href: "/analytics" },
                { name: t("nav.documentation"), href: "/docs" },
            ],
        },
        {
            title: t("nav.profile"),
            items: [
                { name: t("nav.profile"), href: "/profile" },
                { name: t("nav.achievements"), href: "/achievements" },
                { name: t("nav.settings"), href: "/settings" },
            ],
        },
    ];

    const handleLinkClick = () => {
        setIsDropdownOpen(false);
    };

    const handleLanguageChange = (event) => {
        const selectedLanguage = event.target.value;
        setLanguage(selectedLanguage);
        changeLanguage(selectedLanguage);
    };

    const renderFlagIcon = (language) => {
        switch (language) {
            case "en":
                return <FlagIcon code="US" size={16} className="inline-block mr-2" />;
            case "fr":
                return <FlagIcon code="FR" size={16} className="inline-block mr-2" />;
            // Add more cases for other languages if needed
            default:
                return null;
        }
    };

    return (
        <>
            <script type="application/ld+json">
                {JSON.stringify({
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    name: "BidCoin", // Replace with your organization name
                    url: "https://bidcoin.live", // Replace with your organization URL
                    logo: "https://bidcoin.live/logo512.png", // Replace with your logo URL
                    contactPoint: {
                        "@type": "ContactPoint",
                        contactType: "customer service",
                    },

                    email: "admin@bidcoin.live",
                })}
            </script>
            <header className="fixed top-0 left-0 right-0 z-50 bg-zinc-900/75 backdrop-blur-[12px] border-b border-zinc-800/10 rounded-b-2xl">
                <div className="max-w-7xl mx-auto px-0 sm:px-4">
                    <div className="flex items-center justify-between h-24 sm:h-26">
                        {/* Logo and Primary Nav */}
                        <div className="flex items-center space-x-1 sm:space-x-4">
                            {/* Logo with Dropdown */}
                            <div
                                className="group relative flex items-center space-x-1 cursor-pointer py-2 pl-2"
                                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                            >
                                <Menu className="h-5 w-5 text-zinc-300" />
                            </div>
                            <div>
                                <Breadcrumb />

                                {/* Dropdown positioned better for mobile */}
                                <div
                                    className={`${
                                        isDropdownOpen ? "block" : "hidden"
                                    } absolute top-[calc(100%+0.25rem)] left-0 w-40 sm:w-48 py-2 bg-zinc-900/90 backdrop-blur-xl rounded-xl shadow-xl border border-zinc-700/70`}
                                    ref={dropdownRef}
                                >
                                    <Link
                                        to="/"
                                        className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
                                        onClick={handleLinkClick}
                                    >
                                        {t("link.home")}
                                    </Link>
                                    <Link
                                        to="/auctions"
                                        className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
                                        onClick={handleLinkClick}
                                    >
                                        {t("link.auctions")}
                                    </Link>
                                    <Link
                                        to="/profile"
                                        className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
                                        onClick={handleLinkClick}
                                    >
                                        {t("link.profile")}
                                    </Link>
                                    <Link
                                        to="/staking"
                                        className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
                                        onClick={handleLinkClick}
                                    >
                                        {t("link.staking")}
                                    </Link>

                                    <Link
                                        to="/cw"
                                        className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
                                        onClick={handleLinkClick}
                                    >
                                        {t("link.communityHub")}
                                    </Link>
                                    <Link
                                        to="/how-bidcoin-auctions-work"
                                        className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
                                        onClick={handleLinkClick}
                                    >
                                        {t("link.howItWorks")}
                                    </Link>
                                    <a
                                        href="https://whitepaper.bidcoin.live"
                                        className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
                                        onClick={handleLinkClick}
                                    >
                                        {t("link.whitepaper")}
                                    </a>
                                    {/* <Link
                                    to="/term-and-conditions"
                                    className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
                                    onClick={handleLinkClick}
                                >
                                    Contact Us
                                </Link> */}
                                    <Link
                                        to="/presale"
                                        className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
                                        onClick={handleLinkClick}
                                    >
                                        {t("link.presale")}
                                    </Link>
                                    <Link
                                        to="/airdrop"
                                        className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
                                        onClick={handleLinkClick}
                                    >
                                        {t("link.airdrop")}
                                    </Link>
                                    {/* <Link
                                    to="/referral"
                                    className="block px-4 py-2 text-sm text-zinc-300 hover:bg-zinc-700"
                                    onClick={handleLinkClick}
                                >
                                    Referral Program
                                </Link> */}
                                </div>
                            </div>
                        </div>

                        {/* Search Bar and Actions */}
                        <div className="flex items-center space-x-2 sm:space-x-4">
                            {/* Connect Button */}

                            {/* Icons */}
                            <div className="flex flex-col items-end">
                                <div className="flex items-end space-x-0 sm:space-x-0 pr-0">
                                    <Balance />
                                    {/* {isConnected && <SettingsAppHeaderIcon />} */}
                                    {/* <NotificationBell /> */}
                                </div>
                                {/* <div className="flex justify-center">
                                    <AddressDisplay />
                                </div> */}
                                {/* <div className="language-selector flex items-center space-x-2">
                                    <select
                                        value={language}
                                        onChange={handleLanguageChange}
                                        style={{
                                            height: "auto",
                                            width: "auto",
                                            outline: "none",
                                            borderColor: "#374151",
                                            ring: "2px",
                                            ringColor: "#374151",
                                            textAlign: "center",
                                            backgroundColor: "#1f2937",
                                            border: "1px solid #374151",
                                            margin: "0.25rem",
                                            fontSize: "0.875rem",
                                            borderRadius: "0.375rem",
                                            color: "#9ca3af",
                                        }}
                                    >
                                        <option value="en">{t("language.english")}</option>
                                        <option value="fr">{t("language.french")}</option>
                                    </select>
                                    {isConnected && <SettingsAppHeaderIcon />}
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            </header>
        </>
    );
};

export default AppHeader;
